// REACT
import { useContext, useState, useEffect } from "react";
import CreatableSelect from "react-select/creatable";
import { toast } from "react-toastify";

// CONTEXT
import { maincontext } from "../../context/maincontext";

// CUSTOM HOOKS
import { useCreate, useRead, useUpdate } from "../../hooks/useFetch";

// ASSETS
import "../../assets/styles/conflictsForm.css";
import { Button, Form } from "react-bootstrap";
import { FaCheckCircle, FaSave } from "react-icons/fa";
import { MdCancel } from "react-icons/md";
import languagePack from "../../assets/languages/languagePack";
import { FaTag, FaEdit } from "react-icons/fa";

// COMPONENTS
import Loading from "../common/Loading";

export default function ConflictsForm(props) {
    const { language, token } = useContext(maincontext);

    // PRODUCT OPTIONS RENDER
    const [productSelectOptions, setProductSelectOptions] = useState([]);
    const [selectedOption, setSelectedOption] = useState(null);

    // Initialize state based on whether it's an edit or create action
    const [dataPackagePrep, setDataPackagePrep] = useState({
        conflicting_name: props.conflict?.product_name || "",
        resolving_product_id: props.isEdit ? props.conflict?.id || "" : "",
        distributor: props.conflict.distributor
    });

    const [dataPackageUpdatePrep, setDataPackageUpdatePrep] = useState({
        distributor: props.conflict.distributor,
        old_product_id: "",
        new_product_id: "",
        alias: props.conflict?.alias || "",
    });

    // URL & Fetch Hooks
    const productsUrl = `${process.env.REACT_APP_BACKEND_BASE_URL}products`;
    const resolveUrl = `${process.env.REACT_APP_BACKEND_BASE_URL}resolve`;
    const updateResolveUrl = `${process.env.REACT_APP_BACKEND_BASE_URL}aliases`;

    const [dataPackage, setDataPackage] = useState(null);
    const [dataPackageUpdate, setDataPackageUpdate] = useState(null);

    const productOptions = useRead(productsUrl, null);
    const resolveConflict = useCreate(dataPackage, resolveUrl)
    const updateResolveConflict = useUpdate(dataPackageUpdate, updateResolveUrl)

    useEffect(() => {
        if (!productOptions.isPending && productOptions.data) {
            const formattedOptions = productOptions.data.map(product => ({
                value: product.id,
                label: `${product.product_name} [SKU: ${product.sku}]`,
                original: product,
            }));

            setProductSelectOptions(formattedOptions);

            if (props.isEdit && props.conflict?.product_name) {
                const matched = formattedOptions.find(
                    opt => opt.original.product_name === props.conflict.product_name
                );

                if (matched) {
                    setDataPackagePrep(prev => ({
                        ...prev,
                        resolving_product_id: matched.value, // <– this is what you POST
                    }));
                }
            }
        }
    }, [productOptions.isPending, productOptions.data, props.isEdit, props.conflict]);

    // IF EDIT MODE NEED TO MAKE SURE TO CORRECTLY SET THE FIELD AND DATAPACKAGE
    useEffect(() => {
        if (
            props.isEdit &&
            props.conflict &&
            productOptions.data &&
            !productOptions.isPending
        ) {
            const formatted = productOptions.data.map(product => ({
                value: product.id,
                label: `${product.product_name} [SKU: ${product.sku}]`,
                original: product
            }));

            setProductSelectOptions(formatted);

            const matched = formatted.find(
                opt => opt.original?.product_name === props.conflict.product_name
            );
            console.log("matched", matched)

            if (matched) {
                setSelectedOption(matched);
                setDataPackageUpdatePrep({
                    distributor: props.conflict.distributor,
                    old_product_id: matched.original?.id || "",
                    new_product_id: matched.original?.id || "",
                    alias: props.conflict?.alias || "",
                });
            }
        }

        // This handles the normal case (non-edit mode)
        if (!props.isEdit && productOptions.data && !productOptions.isPending) {
            const formatted = productOptions.data.map(product => ({
                value: product.id,
                label: `${product.product_name} [SKU: ${product.sku}]`,
                original: product
            }));

            setProductSelectOptions(formatted);
        }
    }, [props.isEdit, props.conflict, productOptions.data, productOptions.isPending]);

    // Close modal & trigger re-fetch **after API request is complete**
    useEffect(() => {
        if (props.isEdit) {
            if (updateResolveConflict.error && !updateResolveConflict.isPending) {
                toast.error(languagePack[language].toast.errorConflictEdit, {
                    position: "top-center",
                    autoClose: 2000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "colored",
                });
                console.error("❌ Update Error:", updateResolveConflict.error);
            }
            else if (updateResolveConflict.data && !updateResolveConflict.isPending && !updateResolveConflict.error) {
                toast.success(languagePack[language].toast.successfulConflictEdit, {
                    position: "top-center",
                    autoClose: 2000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "colored",
                });
                props.setShowModal(false);
                props.setRefetchTrigger(prev => !prev);
            }
        } else {
            if (resolveConflict.error && !resolveConflict.isPending) {
                toast.error(languagePack[language].toast.errorConflictResolve, {
                    position: "top-center",
                    autoClose: 2000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "colored",
                });
                console.error("❌ Create Error:", resolveConflict.error);
            }
            else if (resolveConflict.data && !resolveConflict.isPending && !resolveConflict.error) {
                toast.success(languagePack[language].toast.successfulConflictResolve, {
                    position: "top-center",
                    autoClose: 2000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "colored",
                });
                props.setShowModal(false);
                props.setRefetchTrigger(prev => !prev);
            }
        }

        // eslint-disable-next-line
    }, [resolveConflict.data, resolveConflict.isPending, resolveConflict.error, updateResolveConflict.data, updateResolveConflict.isPending, updateResolveConflict.error]);

    // Validation Errors
    const [errors, setErrors] = useState({});

    function btnOkHandler() {
        const newErrors = {};

        if (props.isEdit) {
            if (!dataPackageUpdatePrep.new_product_id?.trim()) {
                newErrors.productName = languagePack[language].conflictResolver.formFieldValidation1;
            }
        }
        else {
            if (!dataPackagePrep.resolving_product_id?.trim()) {
                newErrors.productName = languagePack[language].conflictResolver.formFieldValidation1;
            }
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        // Clear errors & prepare request payload
        setErrors({});

        if (props.isEdit) {
            setDataPackageUpdate({ ...dataPackageUpdatePrep });
        } else {
            setDataPackage({ ...dataPackagePrep });
        }
    }

    return (
        <div className="conflictFormModal">
            {token == null || productOptions.isPending ?
                <Loading /> :
                <div className="conflictFormModalContent">
                    <div className="d-flex justify-content-center align-items-center gap-4">
                        {props.isEdit ?
                            <FaEdit className="text-warning " />
                            : <FaTag className="text-success" />
                        }
                        <h2>{props.isEdit ? languagePack[language].conflictResolver.formEditTitle : languagePack[language].conflictResolver.formTitle}</h2>
                    </div>
                    <h4 className="mt-4">{props.isEdit ? props.conflict.alias : props.conflict.product_name}</h4>

                    {/* Form Fields */}
                    <Form.Group className="mb-4 mt-4 formGroup" controlId="conflictName">
                        <Form.Label className="formLabel">
                            {languagePack[language].conflictResolver.formField1}
                            <span className="required">*</span>
                        </Form.Label>

                        <div className={errors.productName ? "is-invalid" : ""}>
                            <CreatableSelect
                                className="conflictDropDown"
                                isDisabled={productOptions.error}
                                isClearable
                                isSearchable
                                options={productSelectOptions}
                                value={selectedOption}
                                onChange={(selected) => {
                                    setSelectedOption(selected);
                                    if (props.isEdit) {
                                        setDataPackageUpdatePrep(prev => ({
                                            ...prev,
                                            new_product_id: selected?.value || ""
                                        }));
                                    }
                                    else {
                                        setDataPackagePrep(prev => ({
                                            ...prev,
                                            resolving_product_id: selected?.value || ""
                                        }));
                                    }

                                }}
                                placeholder={languagePack[language].conflictResolver.placeholder1}
                                classNamePrefix="react-select"
                                formatCreateLabel={(inputValue) =>
                                    `${languagePack[language].conflictResolver.createNewOption}: "${inputValue}"`
                                }
                                filterOption={(option, inputValue) => {
                                    const name = option.data.original?.product_name?.toLowerCase() || "";
                                    const sku = option.data.original?.sku?.toLowerCase() || "";
                                    return (
                                        name.includes(inputValue.toLowerCase()) ||
                                        sku.includes(inputValue.toLowerCase())
                                    );
                                }}
                            />
                            {errors.productName && (
                                <div className="invalid-feedback d-block">{errors.productName}</div>
                            )}
                        </div>
                    </Form.Group>


                    {/* Buttons Positioned at Bottom */}
                    <div className="modalFooter">
                        <Button variant="secondary" className="conflictFormModalCancel" onClick={() => props.setShowModal(false)}>
                            <MdCancel className="create-icon" />&nbsp;{languagePack[language].common.cancel}
                        </Button>

                        <Button
                            variant="primary"
                            className="conflictFormModalOk"
                            onClick={btnOkHandler}
                            disabled={resolveConflict.isPending || updateResolveConflict.isPending}
                        >
                            {props.isEdit ? (
                                <>
                                    {
                                        updateResolveConflict.isPending ? <Loading /> :
                                            <><FaSave className="create-icon" />&nbsp;{languagePack[language].common.save}</>}
                                </>
                            ) : (
                                <>
                                    {
                                        resolveConflict.isPending ? <Loading /> :
                                            <><FaCheckCircle className="create-icon" />&nbsp;{languagePack[language].common.merge}</>}
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            }
        </div>
    );
}
